import {
  enableLinkClickTracking,
  LinkClickTrackingPlugin,
} from '@snowplow/browser-plugin-link-click-tracking';
import {
  addGlobalContexts,
  enableActivityTracking,
  newTracker,
  setUserId,
  type TrackerConfiguration,
} from '@snowplow/browser-tracker';
import { useEffect } from 'react';

import { debugSnowplow } from '../analytics.utils';
import { TRACKER_IDS } from '../constants';
import {
  MINIMUM_VISIT_LENGTH,
  HEARTBEAT_DELAY,
  COMMON_TRACKER_CONFIG,
} from '../snowplow/constants';
import { SNOWPLOW_SCHEMAS } from '../snowplow/schemas';

import type { StateType, AnalyticsParams } from '../AnalyticsProvider.types';

const useTrackerInit = ({
  analytics,
  analyticsParams,
  setAnalytics,
}: {
  analytics: StateType;
  analyticsParams: AnalyticsParams | undefined;
  setAnalytics: React.Dispatch<React.SetStateAction<StateType>>;
}) => {
  useEffect(() => {
    if (!analyticsParams) return;

    const { debug, userId } = analyticsParams;
    const initSP = () => {
      const accounts = [];

      const {
        VITE_SNOWPLOW_ANALYTICS_APP_ID: SNOWPLOW_ANALYTICS_APP_ID,
        VITE_SNOWPLOW_ANALYTICS_COLLECTOR_URL: SNOWPLOW_ANALYTICS_COLLECTOR_URL,
        VITE_SNOWPLOW_ANALYTICS_POST_PATH: SNOWPLOW_ANALYTICS_POST_PATH,
      } = import.meta.env;

      if (
        SNOWPLOW_ANALYTICS_APP_ID &&
        SNOWPLOW_ANALYTICS_COLLECTOR_URL &&
        SNOWPLOW_ANALYTICS_POST_PATH
      ) {
        const sharedTrackerConfig = {
          appId: SNOWPLOW_ANALYTICS_APP_ID,
          collectorUrl: SNOWPLOW_ANALYTICS_COLLECTOR_URL,
          postPath: SNOWPLOW_ANALYTICS_POST_PATH,
        };

        accounts.push(
          { trackerName: TRACKER_IDS.sp.default, ...sharedTrackerConfig },
          { trackerName: TRACKER_IDS.sp.legacy, ...sharedTrackerConfig },
        );
      }

      accounts.forEach((account) => {
        const { appId, collectorUrl, postPath, trackerName } = account;
        const config: TrackerConfiguration = {
          ...COMMON_TRACKER_CONFIG,
          appId,
          postPath,
          plugins: [LinkClickTrackingPlugin()],
        };

        try {
          newTracker(account.trackerName, collectorUrl, config);
          if (trackerName === TRACKER_IDS.sp.default) {
            enableActivityTracking(
              {
                minimumVisitLength: MINIMUM_VISIT_LENGTH,
                heartbeatDelay: HEARTBEAT_DELAY,
              },
              [TRACKER_IDS.sp.default],
            );
          }
          enableLinkClickTracking();

          setAnalytics(({ providers = {} }) => ({
            providers: {
              ...providers,
              sp: {
                isInitialized: true,
                trackers: [
                  ...(providers.sp?.trackers || []),
                  { appId, collectorUrl },
                ],
              },
            },
          }));

          debug && debugSnowplow('initialized with\n%o', { data: config });
        } catch (error) {
          debug &&
            debugSnowplow('failed with\n%o', {
              data: error,
              logger: console.error, // eslint-disable-line no-console
            });
          return false;
        }
      });

      try {
        setUserId(`${userId}`);
        addGlobalContexts([
          {
            schema: SNOWPLOW_SCHEMAS.company,
            data: {
              id: parseInt(analyticsParams?.companyId ?? 0, 10),
            },
          },
          {
            schema: SNOWPLOW_SCHEMAS.user,
            data: {
              id: parseInt(analyticsParams?.userId ?? 0, 10),
            },
          },
        ]);
      } catch (error) {
        debugSnowplow('failed with\n%o', {
          data: error,
          logger: console.error, // eslint-disable-line no-console
        });

        return false;
      }
    };

    !analytics.providers?.sp?.isInitialized && initSP();
  }, [analytics.providers, analyticsParams, setAnalytics]);
};

export default useTrackerInit;
